import React from "react";
import { Composition } from "remotion";
import { buildTimeline, defaultEnd, defaultHead } from "@veyso/timeline";
import { VeysoComposition, calculateVeysoMetadata, type VeysoCompositionProps } from "./VeysoComposition.js";
import { LaunchComposition, calculateLaunchMetadata, sampleLaunchBrief } from "./launch/LaunchComposition.js";
import { GeneratedComposition, calculateGeneratedMetadata } from "./generated/GeneratedComposition.js";

/**
 * A self-contained timeline so `pnpm remotion:studio` opens on something real
 * without needing a database or a rendered project. The renderer overrides
 * `defaultProps` with the project's own timeline.
 */
const previewTimeline = buildTimeline({
  projectName: "Preview",
  body: { assetKey: "sample/body.mp4", duration: 24, audioAssetKey: "sample/body.mp3" },
  head: defaultHead("Ship the update, clearly."),
  end: defaultEnd(),
  transitions: { headToBody: { type: "zoom", duration: 0.6 }, bodyToEnd: { type: "fade", duration: 0.8 } },
  actions: [
    {
      id: "a1",
      timestamp: 4,
      type: "click",
      source: "metadata",
      target: { x: 0.62, y: 0.18, width: 0.16, height: 0.07 },
      label: "Create project",
    },
    {
      id: "a2",
      timestamp: 12,
      type: "typing",
      source: "metadata",
      target: { x: 0.3, y: 0.44, width: 0.28, height: 0.06 },
      label: "Name the project",
    },
  ],
  captions: {
    enabled: true,
    segments: [
      { start: 0, end: 5, text: "First, open the project dashboard." },
      { start: 5, end: 11, text: "Click create project in the top right." },
    ],
  },
}).timeline;

export const RemotionRoot: React.FC = () => (
  <>
    <Composition
      id="VEYSO"
      component={VeysoComposition}
      calculateMetadata={calculateVeysoMetadata}
      // Overwritten by calculateMetadata; Remotion needs placeholders up front.
      durationInFrames={300}
      fps={30}
      width={1920}
      height={1080}
      defaultProps={{ timeline: previewTimeline, assetBaseUrl: "/assets" }}
    />
    {/*
      The launch film. A separate composition rather than a mode of the one
      above, because it shares nothing with it — no timeline, no assets, no
      recording. It is drawn entirely from a brief.
    */}
    <Composition
      id="LAUNCH"
      component={LaunchComposition}
      calculateMetadata={calculateLaunchMetadata}
      durationInFrames={3000}
      fps={60}
      width={1920}
      height={1080}
      defaultProps={{ brief: sampleLaunchBrief }}
    />
    {/*
      A page a model wrote, sampled frame by frame. It has no default worth
      shipping — the studio opens it blank, and every real render passes the
      page in. See `GeneratedComposition` for why this is a composition rather
      than a second capture pipeline.
    */}
    <Composition
      id="GENERATED"
      component={GeneratedComposition}
      calculateMetadata={calculateGeneratedMetadata}
      durationInFrames={300}
      fps={30}
      width={1920}
      height={1080}
      defaultProps={{ html: "<body></body>", fontFaceCss: "", durationInSeconds: 10 }}
    />
  </>
);

export type { VeysoCompositionProps };
